import { useEffect, useState } from "react";
import { Plus, Save, X } from "lucide-react";
import { api, ApiError } from "../../api";
import { Button, Card, Field } from "../../ui";

/**
 * Tier groups own washer/dryer capacity (since migration 0020). The
 * per-riser table below the groups shows each riser's group membership
 * and its physical in-use count; the editable capacity number lives
 * on the group.
 *
 * Admins can create new tier groups inline and (re)assign risers to
 * groups from the riser table's dropdown — no API call needed.
 */

type Building = { id: string; name: string };

type TierGroupRow = {
  id: string;
  code: string;
  name: string;
  washerDryerCapacity: number;
  washerDryerInUse: number;
  memberRiserCodes: string[];
};

type RiserRow = {
  id: string;
  code: string;
  name: string;
  washerDryerInUse: number;
  tierGroupId?: string | null;
};

export function AdminRisers() {
  const [buildings, setBuildings] = useState<Building[]>([]);
  const [buildingId, setBuildingId] = useState<string>("");
  const [groups, setGroups] = useState<TierGroupRow[]>([]);
  const [risers, setRisers] = useState<RiserRow[]>([]);
  const [edits, setEdits] = useState<Record<string, string>>({});
  const [error, setError] = useState<string | null>(null);
  const [creatingGroup, setCreatingGroup] = useState(false);

  useEffect(() => {
    void api.get<{ data: Building[] }>("/api/buildings").then((r) => {
      setBuildings(r.data);
      if (r.data[0]) setBuildingId(r.data[0].id);
    });
  }, []);

  const reload = async (b = buildingId) => {
    if (!b) return;
    const [g, r] = await Promise.all([
      api.get<{ data: TierGroupRow[] }>(
        `/api/admin/buildings/${b}/tier-groups`,
      ),
      api.get<{ data: RiserRow[] }>(`/api/buildings/${b}/risers`),
    ]);
    setGroups(g.data);
    setRisers(r.data);
    const next: Record<string, string> = {};
    for (const row of g.data) next[row.id] = String(row.washerDryerCapacity);
    setEdits(next);
  };

  useEffect(() => {
    if (buildingId) void reload(buildingId);
  }, [buildingId]);

  const save = async (groupId: string) => {
    setError(null);
    const raw = edits[groupId] ?? "";
    const parsed = Number(raw);
    if (raw.trim() === "" || !Number.isInteger(parsed) || parsed < 0) {
      setError("Capacity must be a non-negative integer.");
      return;
    }
    try {
      await api.patch(`/api/admin/tier-groups/${groupId}`, {
        washerDryerCapacity: parsed,
      });
      await reload();
    } catch (err) {
      if (err instanceof ApiError) {
        const body = err.body as { error?: string; currentInUse?: number };
        if (body.error === "capacity_below_current_in_use") {
          setError(
            `Cannot reduce capacity below current in-use (${body.currentInUse}). Decommission existing W/D installs in this tier group first.`,
          );
        } else setError(`Save failed: ${body.error ?? "unknown"}`);
      } else setError("Save failed.");
    }
  };

  const assignRiser = async (
    riserId: string,
    tierGroupId: string | null,
  ) => {
    setError(null);
    try {
      await api.patch(`/api/admin/risers/${riserId}`, { tierGroupId });
      await reload();
    } catch (err) {
      if (err instanceof ApiError) {
        const body = err.body as { error?: string };
        setError(`Assign failed: ${body.error ?? err.status}`);
      } else setError("Assign failed.");
    }
  };

  const ensureElmDefaults = async () => {
    setError(null);
    try {
      const defaults = [
        { code: "1", name: "Risers 01, 02, 03", washerDryerCapacity: 56 },
        { code: "2", name: "Risers 04, 10", washerDryerCapacity: 60 },
        {
          code: "3",
          name: "Risers 05, 06, 07, 08, 09",
          washerDryerCapacity: 150,
        },
      ];
      for (const d of defaults) {
        try {
          await api.post(
            `/api/admin/buildings/${buildingId}/tier-groups`,
            d,
          );
        } catch (err) {
          // code_taken just means the group already exists; ignore.
          if (
            !(
              err instanceof ApiError &&
              (err.body as { error?: string })?.error === "code_taken"
            )
          ) {
            throw err;
          }
        }
      }
      // Reload to get the new group ids, then assign matching risers.
      const g = await api.get<{ data: TierGroupRow[] }>(
        `/api/admin/buildings/${buildingId}/tier-groups`,
      );
      const groupByCode = new Map(g.data.map((x) => [x.code, x.id]));
      const partition: Record<string, string[]> = {
        "1": ["01", "02", "03"],
        "2": ["04", "10"],
        "3": ["05", "06", "07", "08", "09"],
      };
      for (const [groupCode, riserCodes] of Object.entries(partition)) {
        const targetGroupId = groupByCode.get(groupCode);
        if (!targetGroupId) continue;
        for (const riserCode of riserCodes) {
          const r = risers.find((x) => x.code === riserCode);
          if (!r || r.tierGroupId === targetGroupId) continue;
          await api.patch(`/api/admin/risers/${r.id}`, {
            tierGroupId: targetGroupId,
          });
        }
      }
      await reload();
    } catch (err) {
      if (err instanceof ApiError) {
        setError(
          `Setup failed: ${(err.body as { error?: string })?.error ?? err.status}`,
        );
      } else setError("Setup failed.");
    }
  };

  return (
    <div className="flex flex-col gap-4">
      <Card className="!p-0 overflow-hidden">
        <div className="flex items-center justify-between p-5 sm:p-6 border-b border-ink-100">
          <div>
            <h2 className="text-ink-800 m-0">
              Tier groups — washer/dryer capacity
            </h2>
            <p className="text-xs text-ink-500 mt-0.5 max-w-xl">
              W/D capacity is allocated by tier group, not per riser. Each
              group bundles a set of risers that share vertical plumbing
              capacity. The portal blocks new W/D project submissions when
              a tier group is at capacity.
            </p>
          </div>
          <select
            className="select max-w-xs"
            value={buildingId}
            onChange={(e) => setBuildingId(e.target.value)}
          >
            {buildings.map((b) => (
              <option key={b.id} value={b.id}>
                {b.name}
              </option>
            ))}
          </select>
        </div>

        {error && <div className="alert-error mx-5 mt-3">{error}</div>}

        {groups.length === 0 ? (
          <div className="p-5 flex flex-col gap-3">
            <p className="text-[13px] text-ink-500 m-0">
              No tier groups configured for this building yet.
            </p>
            <div className="flex items-center gap-2">
              {!creatingGroup && (
                <Button
                  size="sm"
                  variant="primary"
                  onClick={() => setCreatingGroup(true)}
                >
                  <Plus size={13} /> New tier group
                </Button>
              )}
              <Button
                size="sm"
                variant="secondary"
                onClick={() => void ensureElmDefaults()}
              >
                Set up Elm defaults (1: 56, 2: 60, 3: 150)
              </Button>
            </div>
            <p className="text-[11.5px] text-ink-400 m-0">
              "Elm defaults" creates the three standard groups and
              attaches risers 01–10 to them. Safe to re-run; existing
              groups and assignments are preserved.
            </p>
          </div>
        ) : (
          <>
            <div className="table-wrap">
              <table className="table">
                <thead>
                  <tr>
                    <th>Group</th>
                    <th>Risers</th>
                    <th>In use</th>
                    <th>Capacity</th>
                    <th></th>
                  </tr>
                </thead>
                <tbody>
                  {groups.map((g) => {
                    const dirty =
                      (edits[g.id] ?? "") !== String(g.washerDryerCapacity);
                    const atCap = g.washerDryerInUse >= g.washerDryerCapacity;
                    return (
                      <tr key={g.id}>
                        <td className="font-medium text-ink-800">
                          <div>{g.name}</div>
                          <div className="text-[11.5px] text-ink-400">
                            Code: {g.code}
                          </div>
                        </td>
                        <td className="text-ink-600">
                          <span className="font-mono">
                            {g.memberRiserCodes.join(", ") || "—"}
                          </span>
                        </td>
                        <td>
                          <span
                            className={`badge ${atCap ? "bg-status-rejected-bg text-status-rejected-fg" : "bg-ink-100 text-ink-700"}`}
                          >
                            {g.washerDryerInUse}
                          </span>
                        </td>
                        <td className="w-40">
                          <input
                            className="input"
                            type="text"
                            inputMode="numeric"
                            value={edits[g.id] ?? ""}
                            onChange={(e) =>
                              setEdits({ ...edits, [g.id]: e.target.value })
                            }
                          />
                        </td>
                        <td>
                          <Button
                            size="sm"
                            variant="primary"
                            disabled={!dirty}
                            onClick={() => void save(g.id)}
                          >
                            <Save size={14} /> Save
                          </Button>
                        </td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            </div>
            <div className="px-5 py-3 border-t border-ink-100">
              {creatingGroup ? null : (
                <Button
                  size="sm"
                  variant="secondary"
                  onClick={() => setCreatingGroup(true)}
                >
                  <Plus size={13} /> New tier group
                </Button>
              )}
            </div>
          </>
        )}

        {creatingGroup && (
          <NewTierGroupForm
            buildingId={buildingId}
            onCancel={() => setCreatingGroup(false)}
            onCreated={async () => {
              setCreatingGroup(false);
              await reload();
            }}
          />
        )}
      </Card>

      <Card className="!p-0 overflow-hidden">
        <div className="px-5 py-4 border-b border-ink-100">
          <h3 className="text-ink-800 m-0 text-[15px]">Risers</h3>
          <p className="text-xs text-ink-500 mt-0.5">
            Per-riser W/D counters and tier-group assignment. Each riser
            belongs to at most one tier group; capacity is enforced on
            the group as a whole.
          </p>
        </div>
        {risers.length === 0 ? (
          <p className="p-5 text-[13px] text-ink-500 m-0">No risers.</p>
        ) : (
          <div className="table-wrap">
            <table className="table">
              <thead>
                <tr>
                  <th>Riser</th>
                  <th>Name</th>
                  <th>In use</th>
                  <th>Tier group</th>
                </tr>
              </thead>
              <tbody>
                {risers.map((r) => (
                  <tr key={r.id}>
                    <td className="font-mono text-ink-800">{r.code}</td>
                    <td className="text-ink-600">{r.name}</td>
                    <td>
                      <span className="badge bg-ink-100 text-ink-700">
                        {r.washerDryerInUse}
                      </span>
                    </td>
                    <td>
                      <select
                        className="select py-1 h-[30px] max-w-[260px]"
                        value={r.tierGroupId ?? ""}
                        onChange={(e) =>
                          void assignRiser(r.id, e.target.value || null)
                        }
                        disabled={groups.length === 0}
                      >
                        <option value="">
                          {groups.length === 0
                            ? "— (create groups first)"
                            : "— (no group, no cap)"}
                        </option>
                        {groups.map((g) => (
                          <option key={g.id} value={g.id}>
                            {g.name}
                          </option>
                        ))}
                      </select>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </Card>
    </div>
  );
}

/**
 * Inline form for creating a new tier group. Validates client-side
 * for empty fields + non-negative integer capacity; everything else
 * surfaces via the server error mapping (code_taken is the common one
 * when an admin tries to reuse a code already on the building).
 */
function NewTierGroupForm({
  buildingId,
  onCancel,
  onCreated,
}: {
  buildingId: string;
  onCancel: () => void;
  onCreated: () => void;
}) {
  const [code, setCode] = useState("");
  const [name, setName] = useState("");
  const [capacity, setCapacity] = useState("");
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const submit = async () => {
    setError(null);
    if (!code.trim()) return setError("Code is required.");
    if (!name.trim()) return setError("Name is required.");
    const cap = Number(capacity);
    if (capacity.trim() === "" || !Number.isInteger(cap) || cap < 0) {
      return setError("Capacity must be a non-negative integer.");
    }
    setSubmitting(true);
    try {
      await api.post(`/api/admin/buildings/${buildingId}/tier-groups`, {
        code: code.trim(),
        name: name.trim(),
        washerDryerCapacity: cap,
      });
      onCreated();
    } catch (err) {
      if (err instanceof ApiError) {
        const c = (err.body as { error?: string })?.error;
        setError(
          c === "code_taken"
            ? `A group with code "${code.trim()}" already exists in this building.`
            : `Create failed: ${c ?? err.status}`,
        );
      } else setError("Create failed.");
      setSubmitting(false);
    }
  };

  return (
    <div className="border-t border-ink-100 bg-sand-50 px-5 py-4">
      <div className="flex items-center gap-2 mb-3">
        <strong className="text-[13px] text-ink-800">New tier group</strong>
        <Button
          size="sm"
          variant="ghost"
          onClick={onCancel}
          className="ml-auto"
        >
          <X size={13} />
        </Button>
      </div>
      <div className="grid grid-cols-1 sm:grid-cols-[120px_1fr_140px_auto] gap-2 items-end">
        <Field label="Code">
          <input
            className="input"
            value={code}
            onChange={(e) => setCode(e.target.value)}
            placeholder="e.g. 1"
            autoFocus
          />
        </Field>
        <Field label="Name">
          <input
            className="input"
            value={name}
            onChange={(e) => setName(e.target.value)}
            placeholder="e.g. Risers 01, 02, 03"
          />
        </Field>
        <Field label="Capacity">
          <input
            className="input"
            inputMode="numeric"
            value={capacity}
            onChange={(e) => setCapacity(e.target.value)}
            placeholder="56"
          />
        </Field>
        <Button
          size="sm"
          variant="primary"
          loading={submitting}
          onClick={() => void submit()}
        >
          <Plus size={13} /> Create
        </Button>
      </div>
      {error && (
        <p className="text-[12px] text-status-rejected-fg m-0 mt-2">{error}</p>
      )}
    </div>
  );
}
